import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { useApi } from "../../lib/useApi";
import { api, formatApiError } from "../../lib/api";
import { usePlatformCan } from "../../lib/platform";
import { Loader, ErrorState, PageHeader } from "../../components/common/States";
import { Summary, Observations } from "../../components/insights/InsightParts";
import { Kpi } from "../../components/stats/StatsParts";

const NUMS = [
  ["max_feedbacks_per_analysis", "Commentaires max. par synthèse"], ["company_daily_analyses", "Synthèses par entreprise et par jour"],
  ["platform_monthly_calls", "Appels IA max. par mois (plateforme)"], ["monthly_budget_usd", "Budget mensuel (USD, estimation)"],
  ["price_in_per_mtok", "Prix entrée / million de jetons (USD)"], ["price_out_per_mtok", "Prix sortie / million de jetons (USD)"],
  ["timeout_seconds", "Délai maximal d'un appel (s)"],
];

const SettingsCard = ({ cfg, canManage, onSaved }) => {
  const [f, setF] = useState(cfg.settings);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setF(cfg.settings); }, [cfg]);
  const save = async () => {
    setBusy(true);
    try { await api.put("/admin/ai/settings", { ...f, ...Object.fromEntries(NUMS.map(([k]) => [k, Number(f[k])])) }); toast.success("Paramètres IA enregistrés"); onSaved(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="card-surface space-y-4 p-6" data-testid="ai-settings">
      <p className={`rounded-xl px-3 py-2 text-sm ${cfg.status.available ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-900"}`} data-testid="ai-settings-status">
        {cfg.status.available ? "Service IA opérationnel." : cfg.status.reason} Clé fournisseur : {cfg.key_configured ? "configurée côté serveur (jamais affichée)" : "absente"}.</p>
      <label className="flex items-center justify-between text-sm"><span><b>Activer l'analyse par IA</b></span><Switch checked={f.enabled} disabled={!canManage} onCheckedChange={(v) => setF({ ...f, enabled: v })} data-testid="ai-enabled" /></label>
      <label className="flex items-center justify-between text-sm"><span><b>Classer automatiquement chaque nouveau feedback</b><span className="block text-xs text-slate-500">Thème, sentiment, résumé. Les dossiers confidentiels ne sont jamais transmis.</span></span>
        <Switch checked={f.auto_classify} disabled={!canManage} onCheckedChange={(v) => setF({ ...f, auto_classify: v })} data-testid="ai-auto-classify" /></label>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-xs text-slate-500">Fournisseur<select className="field-select mt-1 w-full" value={f.provider} disabled={!canManage} onChange={(e) => setF({ ...f, provider: e.target.value, model: cfg.providers[e.target.value][0] })} data-testid="ai-provider">
          {Object.keys(cfg.providers).map((p) => <option key={p} value={p}>{cfg.provider_labels[p]}</option>)}</select></label>
        <label className="text-xs text-slate-500">Modèle<select className="field-select mt-1 w-full" value={f.model} disabled={!canManage} onChange={(e) => setF({ ...f, model: e.target.value })} data-testid="ai-model">
          {cfg.providers[f.provider].map((m) => <option key={m} value={m}>{m}</option>)}</select></label>
        {NUMS.map(([k, label]) => <label key={k} className="text-xs text-slate-500">{label}<Input type="number" min={0} step="any" value={f[k]} disabled={!canManage} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`ai-${k}`} /></label>)}
      </div>
      {canManage && <Button onClick={save} disabled={busy} className="rounded-full" data-testid="ai-settings-save">{busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}Enregistrer</Button>}
    </div>
  );
};

const Usage = () => {
  const { data } = useApi("/admin/ai/usage");
  if (!data) {
    return <Loader />;
  }
  return (
    <div className="card-surface space-y-4 p-6" data-testid="ai-usage">
      <p className="font-display font-semibold">Consommation du mois {data.month}</p>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Kpi testId="ai-usage-calls" label="Appels réussis" value={data.calls} /><Kpi testId="ai-usage-errors" label="Erreurs" value={data.errors} />
        <Kpi testId="ai-usage-cost" label="Coût estimé (USD)" value={data.cost_usd} /><Kpi testId="ai-usage-tokens" label="Jetons (estimation)" value={data.tokens} />
      </div>
      <ul className="divide-y divide-slate-100 text-sm" data-testid="ai-usage-companies">{data.by_company.map((b) => <li key={b.key} className="flex justify-between py-1.5"><span>{b.label}</span><span className="text-slate-500">{b.calls} appels · {b.errors} erreurs · {b.cost_usd} USD</span></li>)}</ul>
      {data.recent_errors.length > 0 && <div><p className="text-sm font-semibold">Dernières erreurs</p><ul className="text-xs text-rose-700">{data.recent_errors.map((e) => <li key={e.at}>{new Date(e.at).toLocaleString("fr-FR")} · {e.company} · {e.error}</li>)}</ul></div>}
    </div>
  );
};

const Network = () => {
  const ref = useApi("/public/reference");
  const companies = useApi("/admin/companies?status=active");
  const [f, setF] = useState({ date_from: "", date_to: "", company_id: "", sector: "" });
  const qs = new URLSearchParams(Object.entries(f).filter(([, v]) => v)).toString();
  const { data, loading, error, reload } = useApi(`/admin/insights/overview?${qs}`);
  const list = Array.isArray(companies.data) ? companies.data : companies.data?.items || [];
  return (
    <div className="space-y-6" data-testid="ai-network">
      <div className="card-surface grid gap-2 p-4 sm:grid-cols-2 lg:grid-cols-4">
        <label className="text-xs text-slate-500">Du<Input type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} data-testid="ai-net-from" /></label>
        <label className="text-xs text-slate-500">Au<Input type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} data-testid="ai-net-to" /></label>
        <select className="field-select self-end" value={f.sector} onChange={(e) => setF({ ...f, sector: e.target.value, company_id: "" })} data-testid="ai-net-sector">
          <option value="">Tous secteurs</option>{(ref.data?.sectors || []).map((s) => <option key={s.code} value={s.label}>{s.label}</option>)}</select>
        <select className="field-select self-end" value={f.company_id} onChange={(e) => setF({ ...f, company_id: e.target.value })} data-testid="ai-net-company">
          <option value="">Toutes entreprises</option>{list.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>
      </div>
      {loading && !data && <Loader />}
      {error && <ErrorState message={error} onRetry={reload} />}
      {data && <><p className="text-xs text-slate-500">{data.companies_in_scope} entreprise(s) dans le périmètre · aucun texte d'avis n'est affiché ni transmis depuis cette vue.</p><Summary o={data} /><Observations o={data} admin /></>}
    </div>
  );
};

export default function AdminAI() {
  const can = usePlatformCan();
  const cfg = useApi("/admin/ai/settings");
  return (
    <div className="space-y-8">
      <PageHeader title="Intelligence artificielle" description="Fournisseur, quotas, coûts et vue réseau des analyses. Les clés restent côté serveur." />
      {cfg.loading && !cfg.data && <Loader />}
      {cfg.error && <ErrorState message={cfg.error} onRetry={cfg.reload} />}
      {cfg.data && <div className="grid gap-6 lg:grid-cols-2"><SettingsCard cfg={cfg.data} canManage={can("settings:manage")} onSaved={cfg.reload} /><Usage /></div>}
      <Network />
    </div>
  );
}
